// MOCK / FRONTEND-ONLY — no backend field exists yet for a per-organisation AI
// models budget/allocation. This module holds that state locally (in memory, keyed
// by tenantId) so the AI Models column + modal have something real to read/write
// during this session, without persisting anything or touching the backend. Replace
// with a real API-backed store once the backend supports it.
export const AI_AGENTS = [
  { key: "report_writer", label: "Report Writer" },
  { key: "chat", label: "Chat" },
  { key: "mapping", label: "Mapping" },
  { key: "review", label: "Review" },
];

export const MODEL_TIERS = ["High", "Medium", "Low"];

const DEFAULT_PCT = 100 / AI_AGENTS.length; // 25% each for 4 agents

function buildDefaultAllocation() {
  return {
    annualLimitGbp: null,
    contractStartDate: null,
    agents: AI_AGENTS.map((agent) => ({ key: agent.key, pct: DEFAULT_PCT, tier: "Medium" })),
  };
}

// In-memory store, keyed by tenantId. Module-level (not React state) so it survives
// the popup closing/reopening within the same session — same tradeoff as any other
// "frontend-only for now" mock store on this page.
const allocationsByTenantId = new Map();

export function getAiModelsAllocation(tenantId) {
  if (!tenantId) return buildDefaultAllocation();
  if (!allocationsByTenantId.has(tenantId)) {
    allocationsByTenantId.set(tenantId, buildDefaultAllocation());
  }
  return allocationsByTenantId.get(tenantId);
}

export function setAiModelsAllocation(tenantId, allocation) {
  if (!tenantId) return;
  allocationsByTenantId.set(tenantId, allocation);
}

export function formatAnnualLimitGbp(annualLimitGbp) {
  if (annualLimitGbp === null || annualLimitGbp === undefined || annualLimitGbp === "") return "Not set";
  const num = Number(annualLimitGbp);
  if (Number.isNaN(num)) return "Not set";
  return `£${num.toLocaleString("en-GB", { maximumFractionDigits: 0 })}/yr`;
}

/**
 * Splits a total usage % across an org's configured AI agents, in proportion to
 * each agent's allocated % of the total limit (see AiModelsPopup.jsx) — e.g. if
 * Report Writer is allocated 40% of the budget and the org used 60% of its total
 * allowance for the period, Report Writer's segment is 0.4 * 60% = 24 percentage
 * points of the stacked bar. Used by the AI Status dashboard's "By Task" view.
 * @param {number} totalPct
 * @param {{key: string, pct: number, tier: string}[]} agents
 * @returns {{task_type: string, pct: number}[]}
 */
export function splitPctByAgents(totalPct, agents) {
  return (agents || []).map((agent) => ({
    task_type: agent.key,
    pct: Math.round(totalPct * (agent.pct / 100) * 10) / 10,
  }));
}

// MOCK — there's no real per-tier cost breakdown anywhere (an agent has ONE tier,
// not a % split across tiers), so this is an invented, fixed ratio used purely to
// illustrate the "By Model" view on the AI Status dashboard. Real once model-level
// cost tracking exists.
const MOCK_TIER_SPLIT_RATIO = { High: 0.5, Medium: 0.32, Low: 0.18 };

/**
 * Splits a total usage % across model tiers (High/Medium/Low) using a fixed mock
 * ratio, rounded so the segments always sum to exactly `totalPct` (rounding error is
 * absorbed into the last tier). See MOCK_TIER_SPLIT_RATIO — mock data, not derived
 * from any real per-model cost. Used by the AI Status dashboard's "By Model" view.
 * @param {number} totalPct
 * @returns {{tier: string, pct: number}[]}
 */
export function splitPctByTier(totalPct) {
  const tiers = Object.keys(MOCK_TIER_SPLIT_RATIO);
  const rows = tiers.map((tier) => ({
    tier,
    pct: Math.round(totalPct * MOCK_TIER_SPLIT_RATIO[tier] * 10) / 10,
  }));
  const roundedSum = rows.reduce((sum, row) => sum + row.pct, 0);
  const roundedTotal = Math.round(totalPct * 10) / 10;
  rows[rows.length - 1].pct = Math.round((rows[rows.length - 1].pct + (roundedTotal - roundedSum)) * 10) / 10;
  return rows;
}

// MOCK — there's no real per-user cost breakdown anywhere (usage isn't attributed
// to individual users at the API level), so this is an invented, fixed ratio used
// purely to illustrate the "By Person" view on the AI Status dashboard. Real once
// per-user cost tracking exists. Keys match personTypes.js PERSON_TYPES.
const MOCK_PERSON_SPLIT_RATIO = { "admin@esg360.io": 0.6, "hannah@esg360.io": 0.4 };

/**
 * Splits a total usage % across people using a fixed mock ratio, rounded so the
 * segments always sum to exactly `totalPct` (rounding error absorbed into the last
 * person). See MOCK_PERSON_SPLIT_RATIO — mock data, not derived from any real
 * per-user cost. Used by the AI Status dashboard's "By Person" view.
 * @param {number} totalPct
 * @returns {{person: string, pct: number}[]}
 */
export function splitPctByPerson(totalPct) {
  const people = Object.keys(MOCK_PERSON_SPLIT_RATIO);
  const rows = people.map((person) => ({
    person,
    pct: Math.round(totalPct * MOCK_PERSON_SPLIT_RATIO[person] * 10) / 10,
  }));
  const roundedSum = rows.reduce((sum, row) => sum + row.pct, 0);
  const roundedTotal = Math.round(totalPct * 10) / 10;
  rows[rows.length - 1].pct = Math.round((rows[rows.length - 1].pct + (roundedTotal - roundedSum)) * 10) / 10;
  return rows;
}
